import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, Mail, Phone, MapPin, Briefcase, X, Clock, User as UserIcon, Copy, Check, Eye, EyeOff, Download} from "lucide-react";

import { getTrombinoscopeRequest, getUserJobHistoryRequest } from "../../../redux/actions/userActions";
import "./Trombinoscope.css";
import { API_BASE_URL } from "../../../../api";

export default function Trombinoscope() {
    const dispatch = useDispatch();
    const { trombinoscope, trombinoscopeLoading, trombinoscopeError } = useSelector((s) => s.user);

    const [query, setQuery]             = useState("");
    const [entityFilter, setEntityFilter] = useState("ALL");
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showModal, setShowModal]     = useState(false);
    const [activeTab, setActiveTab]     = useState("rh");
    const [copiedField, setCopiedField] = useState(null);

    useEffect(() => { dispatch(getTrombinoscopeRequest()); }, [dispatch]);

    useEffect(() => {
        if (!showModal) return;
        const fn = (e) => { if (e.key === "Escape") closeModal(); };
        window.addEventListener("keydown", fn);
        return () => window.removeEventListener("keydown", fn);
    }, [showModal]);

    const entities = useMemo(() => {
        const map = new Map();
        (trombinoscope || []).forEach((u) => {
            const code = u.entityCode || "NO_ENTITY";
            const name = u.entityName || "Sans département";
            map.set(code, name);
        });
        return [
            { code: "ALL", name: "Tous les départements" },
            ...Array.from(map.entries()).map(([code, name]) => ({ code, name })),
        ];
    }, [trombinoscope]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (trombinoscope || []).filter((u) => {
            if (entityFilter !== "ALL" && (u.entityCode || "NO_ENTITY") !== entityFilter) return false;
            if (!q) return true;
            const full   = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
            const email  = (u.email || "").toLowerCase();
            const city   = (u.city || "").toLowerCase();
            const entity = (u.entityName || "").toLowerCase();
            const job    = (u.jobName || u.jobCode || "").toLowerCase();
            return full.includes(q) || email.includes(q) || city.includes(q) || entity.includes(q) || job.includes(q);
        });
    }, [trombinoscope, query, entityFilter]);

    const initials = (u) =>
        `${(u?.firstName?.[0] || "U").toUpperCase()}${(u?.lastName?.[0] || "").toUpperCase()}`;

    const nameOf = (u) =>
        `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "Utilisateur";

    const formatDate = (d) => {
        if (!d) return null;
        return new Date(d).toLocaleDateString("fr-FR");
    };

    const openEmployeeModal = (employee) => {
        setSelectedEmployee(employee);
        setShowModal(true);
        setActiveTab("rh");
        document.body.style.overflow = "hidden";
        dispatch(getUserJobHistoryRequest(employee.id));
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedEmployee(null);
        setCopiedField(null);
        setRevealedField(null);
        document.body.style.overflow = "";
    };

    const history        = useSelector((s) => s.user?.historyByUserId?.[selectedEmployee?.id] || []);
    const loadingHistory = useSelector((s) => s.user?.loadingByUserId?.[selectedEmployee?.id]);
    const historyError   = useSelector((s) => s.user?.errorByUserId?.[selectedEmployee?.id]);

    const avatarPalette = [
        "#e8edf5", "#e8f0ee", "#f0ece8", "#ece8f0",
        "#e8eef0", "#f0ede8", "#eaecf5", "#e8f0e8",
    ];
    const avatarColor = (u) => avatarPalette[(u?.id || 0) % avatarPalette.length];

    const nd = <span className="trombiModal__nd">Non renseigné</span>;
    const val = (v) => v || nd;

    const maskRib = (rib) => {
        const raw = String(rib || "").trim();
        if (!raw) return null;
        return `${raw.slice(0, 3)}${raw.slice(3, -2).replace(/\S/g, "*")}${raw.slice(-2)}`;
    };

    const fileNameOf = (path) => {
        const raw = String(path || "").trim();
        if (!raw) return null;
        return raw.split(/[\\/]/).pop();
    };

    const [revealedField, setRevealedField] = useState(null);

    const copyToClipboard = async (value, field) => {
        if (!value) return;
        try {
            await navigator.clipboard.writeText(String(value));
            setCopiedField(field);
            window.setTimeout(() => setCopiedField(null), 1200);
        } catch (e) {
            setCopiedField(null);
        }
    };

    const tabs = [
        { key: "rh",      label: "Données RH" },
        { key: "contrat", label: "Contrat" },
        { key: "perso",   label: "Infos personnelles" },
        { key: "history", label: "Historique postes" },
    ];

    return (
        <div className="trombi">
            {/* TOOLBAR */}
            <div className="trombi__toolbar">
                <div className="trombi__toolbarInner">
                    <div className="trombi__heading">
                        <h1 className="trombi__title">Trombinoscope</h1>
                        {/*{!trombinoscopeLoading && (*/}
                        {/*    <span className="trombi__count">{filtered.length} collaborateur{filtered.length !== 1 ? "s" : ""}</span>*/}
                        {/*)}*/}
                    </div>
                    <div className="trombi__controls">
                        <div className="trombi__searchWrap">
                            <Search className="trombi__searchIcon" size={15} />
                            <input
                                className="trombi__search"
                                placeholder="Nom, poste, département…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            {query && (
                                <button className="trombi__clearBtn" onClick={() => setQuery("")} aria-label="Effacer">
                                    <X size={13} />
                                </button>
                            )}
                        </div>
                        <select className="trombi__select" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
                            {entities.map((e) => <option key={e.code} value={e.code}>{e.name}</option>)}
                        </select>
                    </div>
                </div>
                {trombinoscopeLoading && <div className="trombi__status">Chargement…</div>}
                {trombinoscopeError   && <div className="trombi__status trombi__status--error">{trombinoscopeError}</div>}
            </div>

            {/* GRID */}
            <div className="trombi__grid">
                {filtered.map((u) => {
                    const img  = u?.profilePictureUrl ? `${API_BASE_URL}/api/users/images/profile/${u.profilePictureUrl}` : null;
                    const name = nameOf(u);
                    const role = u.jobName || u.jobCode || "—";
                    const dept = u.entityName || "Sans département";
                    const city = u.city || "—";
                    const bg   = avatarColor(u);

                    return (
                        <button key={u.id} className="trombi__card" onClick={() => openEmployeeModal(u)} type="button">
                            <div className="trombi__cardPhoto" style={{ "--av-bg": bg }}>
                                {img
                                    ? <img src={img} alt={name} className="trombi__cardPhotoImg"
                                           onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                                    : null
                                }
                                <span className="trombi__cardPhotoInitials" style={{ display: img ? "none" : "flex" }}>
                                    {initials(u)}
                                </span>
                            </div>
                            <div className="trombi__cardBody">
                                <div className="trombi__cardName">{name}</div>
                                <div className="trombi__cardRole">{role}</div>
                                <div className="trombi__cardMeta">
                                    <span className="trombi__metaItem"><Briefcase size={11} />{dept}</span>
                                    <span className="trombi__metaItem"><MapPin size={11} />{city}</span>
                                    <span className="trombi__metaItem"><Clock size={11} />{u.seniority || "—"}</span>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {!trombinoscopeLoading && filtered.length === 0 && (
                <div className="trombi__empty">Aucun collaborateur trouvé.</div>
            )}

            {/* MODAL */}
            {showModal && selectedEmployee && (() => {
                const emp = selectedEmployee;
                const imgUrl = emp.profilePictureUrl
                    ? `${API_BASE_URL}/api/users/images/profile/${emp.profilePictureUrl}`
                    : null;

                return (
                    <div className="trombiModal" role="dialog" aria-modal="true">
                        <div className="trombiModal__overlay" onClick={closeModal} />
                        <div className="trombiModal__panel">

                            {/* Header */}
                            <div className="trombiModal__header">
                                <div className="trombiModal__headerAvatar" style={{"--av-bg": avatarColor(emp)}}>
                                    {imgUrl
                                        ? <img src={imgUrl} alt="" className="trombiModal__profileAvatarImg"
                                               onError={(e) => {
                                                   e.currentTarget.style.display = "none";
                                                   e.currentTarget.nextSibling.style.display = "flex";
                                               }}/>
                                        : null
                                    }
                                    <span className="trombiModal__profileAvatarInitials"
                                          style={{display: imgUrl ? "none" : "flex"}}>
            {initials(emp)}
        </span>
                                </div>
                                <div className="trombiModal__headerInfo">
                                    <div className="trombiModal__headerName">{nameOf(emp)}</div>
                                    <div className="trombiModal__headerJob">{emp.jobName || emp.jobCode || "—"}</div>
                                    {emp.entityName && <div className="trombiModal__headerDept">{emp.entityName}</div>}
                                </div>
                                <button className="trombiModal__closeBtn" onClick={closeModal} aria-label="Fermer">
                                    <X size={15}/>
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="trombiModal__tabs">
                                {tabs.map((t) => (
                                    <button
                                        key={t.key}
                                        className={`trombiModal__tab ${activeTab === t.key ? "is-active" : ""}`}
                                        onClick={() => setActiveTab(t.key)}
                                        type="button"
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>

                            {/* Body */}
                            <div className="trombiModal__body">

                                {/* ── Onglet Données RH ── */}
                                {activeTab === "rh" && (
                                    <div className="trombiModal__tabContent">
                                        {/* Avatar centré */}
                                        <div className="trombiModal__profileTop">
                                            <div className="trombiModal__profileAvatar" style={{ "--av-bg": avatarColor(emp) }}>
                                                {imgUrl
                                                    ? <img src={imgUrl} alt="" className="trombiModal__profileAvatarImg"
                                                           onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                                                    : null
                                                }
                                                <span className="trombiModal__profileAvatarInitials" style={{ display: imgUrl ? "none" : "flex" }}>
                                                    {initials(emp)}
                                                </span>
                                            </div>
                                            <div className="trombiModal__profileName">{nameOf(emp)}</div>
                                        </div>

                                        {/* Table RH */}
                                        <div className="trombiModal__table">
                                            {[
                                                { label: "Poste actuel",    value: emp.jobName || emp.jobCode },
                                                { label: "Département",     value: emp.entityName },
                                                { label: "Localisation",    value: emp.city },
                                                { label: "Email pro",       value: emp.email, href: `mailto:${emp.email}` },
                                                { label: "Téléphone pro",   value: emp.phone, href: `tel:${emp.phone}` },
                                                { label: "Ancienneté",      value: emp.seniority },
                                            ].map(({ label, value, href }) => (
                                                <div className="trombiModal__row" key={label}>
                                                    <span className="trombiModal__rowLabel">{label}</span>
                                                    <span className="trombiModal__rowValue">
                                                        {href && value
                                                            ? <a href={href} className="trombiModal__link">{value}</a>
                                                            : val(value)
                                                        }
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── Onglet Contrat ── */}
                                {activeTab === "contrat" && (
                                    <div className="trombiModal__tabContent">
                                        <div className="trombiModal__sectionTitle"><Briefcase size={14} /> Informations contrat</div>
                                        <div className="trombiModal__table">
                                            {[
                                                { label: "Code employé",    value: emp.employeeCode },
                                                { label: "Date d'embauche", value: formatDate(emp.hireDate) },
                                                { label: "Type de contrat", value: emp.typeContrat },
                                                { label: "Catégorie",       value: emp.category },
                                                { label: "Statut",          value: emp.status },
                                                { label: "Attestation de RIB", value: emp.attestationRib, isAttestationRib: true },
                                                { label: "RIB",             value: emp.rib, isRib: true },
                                                { label: "IBAN",            value: emp.iban },
                                            ].map(({ label, value, isRib, isAttestationRib }) => (
                                                <div className="trombiModal__row" key={label}>
                                                    <span className="trombiModal__rowLabel">{label}</span>
                                                    <span className="trombiModal__rowValue">
                                                        {isRib && value ? (
                                                            <span className="trombiModal__sensitiveValue">
        <span className="trombiModal__maskedValue">
            {revealedField === "rib" ? value : maskRib(value)}
        </span>
        <button
            type="button"
            className="trombiModal__copyBtn"
            onClick={() => setRevealedField(revealedField === "rib" ? null : "rib")}
            aria-label={revealedField === "rib" ? "Masquer le RIB" : "Afficher le RIB"}
            title={revealedField === "rib" ? "Masquer le RIB" : "Afficher le RIB"}
        >
            {revealedField === "rib" ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
        <button
            type="button"
            className="trombiModal__copyBtn"
            onClick={() => copyToClipboard(value, "rib")}
            aria-label="Copier le RIB"
            title="Copier le RIB"
        >
            {copiedField === "rib" ? <Check size={13} /> : <Copy size={13} />}
        </button>
    </span>
                                                        ) : isAttestationRib && value ? (
                                                            <a
                                                                href={`${API_BASE_URL}/api/users/documents/attestation-rib/${encodeURIComponent(value)}`}
                                                                className="trombiModal__link"
                                                                download={fileNameOf(value)}
                                                            >
                                                                <Download size={13} /> {fileNameOf(value)}
                                                            </a>
                                                        ) : val(value)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── Onglet Infos personnelles ── */}
                                {activeTab === "perso" && (
                                    <div className="trombiModal__tabContent">
                                        <div className="trombiModal__sectionTitle"><UserIcon size={14} /> Informations personnelles</div>
                                        <div className="trombiModal__table">
                                            {[
                                                { label: "Prénom",              value: emp.firstName },
                                                { label: "Nom",                 value: emp.lastName },
                                                { label: "Date de naissance",   value: formatDate(emp.dateOfBirth) },
                                                { label: "CIN",                 value: emp.cin },
                                                { label: "CNSS",                value: emp.cnss },
                                                { label: "Passeport",           value: emp.passeport },
                                                { label: "Carte sejour",        value: emp.carteSejour },
                                                { label: "Statut marital",      value: emp.statutMarital },
                                                { label: "Nombre d'enfants",    value: emp.nombreEnfants != null ? String(emp.nombreEnfants) : null },
                                                { label: "Adresse",             value: emp.adresse },
                                                { label: "Code postal",         value: emp.codePostal },
                                                { label: "Ville",               value: emp.city },
                                                { label: "Mail personnel",      value: emp.email, href: `mailto:${emp.email}` },
                                                { label: "Portable perso",      value: emp.phone, href: `tel:${emp.phone}` },
                                                { label: "Personne d'urgence",  value: emp.personneUrgence },
                                                { label: "Téléphone urgence",   value: emp.phoneUrgence },
                                            ].map(({ label, value, href }) => (
                                                <div className="trombiModal__row" key={label}>
                                                    <span className="trombiModal__rowLabel">{label}</span>
                                                    <span className="trombiModal__rowValue">
                                                        {href && value
                                                            ? <a href={href} className="trombiModal__link">{value}</a>
                                                            : val(value)
                                                        }
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* raccourcis contact */}
                                        <div className="trombiModal__contactChips">
                                            {emp.email && (
                                                <a href={`mailto:${emp.email}`} className="trombiModal__chip">
                                                    <Mail size={13} /> {emp.email}
                                                </a>
                                            )}
                                            {emp.phone && (
                                                <a href={`tel:${emp.phone}`} className="trombiModal__chip trombiModal__chip--phone">
                                                    <Phone size={13} /> {emp.phone}
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ── Onglet Historique postes ── */}
                                {activeTab === "history" && (
                                    <div className="trombiModal__tabContent">
                                        <div className="trombiModal__sectionTitle"><Clock size={14} /> Historique des postes</div>

                                        {loadingHistory && <div className="trombiModal__hint">Chargement…</div>}
                                        {historyError   && <div className="trombiModal__hint trombiModal__hint--err">{historyError}</div>}

                                        {!loadingHistory && !historyError && (
                                            history.length
                                                ? (
                                                    <div className="trombiTimeline">
                                                        {history.map((h, idx) => (
                                                            <div className="trombiTimeline__item" key={h.id || idx}>
                                                                <div className="trombiTimeline__dotWrap">
                                                                    <div className={`trombiTimeline__dot ${h.endDate == null ? "is-current" : ""}`} />
                                                                    {idx < history.length - 1 && <div className="trombiTimeline__line" />}
                                                                </div>
                                                                <div className="trombiTimeline__card">
                                                                    <div className="trombiTimeline__job">
                                                                        {h.jobName || h.jobCode || "—"}
                                                                        {h.endDate == null && <span className="trombiTimeline__badge">Actuel</span>}
                                                                    </div>
                                                                    {h.entityName && <div className="trombiTimeline__dept">Département : {h.entityName}</div>}
                                                                    <div className="trombiTimeline__dates">
                                                                        Du {h.startDate || "—"} {h.endDate ? `au ${h.endDate}` : "à aujourd'hui"}
                                                                    </div>
                                                                    {h.managerName && <div className="trombiTimeline__manager">Manager : <strong>{h.managerName}</strong></div>}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )
                                                : <div className="trombiModal__hint">Aucun historique disponible.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
